import Form, {
    ButtonItem,
    ButtonOptions,
    EmailRule,
    GroupItem,
    Item,
    Label,
    PatternRule,
    RequiredRule,
    SimpleItem
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import React, { useEffect, useRef, useState } from "react";
import FileUploader from 'devextreme-react/file-uploader';
import { ValidationSummary } from "devextreme-react/validation-summary";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMyAccountInformation } from "../api/get-my-account-information";
import { useUpdateMyAccountInformationMutation } from "../api/update-my-account-information";
import { useAuth } from "../../../../contexts/auth";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { formatPhoneNumber } from "../../../../utils/string-utils";

const firstNameEditorOptions = {
    placeholder: 'First Name',
    mode: 'text',
    validationMessagePosition: 'down'
};
const lastNameEditorOptions = {
    placeholder: 'Last Name',
    mode: 'text',
    validationMessagePosition: 'down'
};
const emailEditorOptions = {
    placeholder: 'Email Address',
    mode: 'email',
    validationMessagePosition: 'down'
};
const phoneEditorOptions = {
    placeholder: 'Phone',
    mode: 'phone',
    mask: 'X00-000-0000',
    maskRules: { X: /[02-9]/ },
    useMaskedValue: true,
    validationMessagePosition: 'down'
};
const userNameEditorOptions = {
    placeholder: 'User Name',
    mode: 'text',
    disabled: true,
    validationMessagePosition: 'down'
};

const submitButtonAttributes = { class: 'submit-button' };

export default function AccountPropertiesControl() {

    const [selectedFile, setSelectedFile] = useState<File>();
    const [preview, setPreview] = useState<string>()
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const information = useGetMyAccountInformation();
    const mutation = useUpdateMyAccountInformationMutation();
    const [, updateState] = React.useState({});
    const formData = useRef({ firstName: '', lastName: '', phone: '', email: '', userName: '' });
    const { user } = useAuth();

    useEffect(() => {
        if (!selectedFile) {
            setPreview(undefined);
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        setPreview(objectUrl);

        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFile])

    useEffect(() => {
        if (mutation.isLoading)
            return;

        if (mutation.data === undefined)
            return;

        if (mutation.data?.isOk) {
            queryClient.invalidateQueries(['current-user']);
            showSuccessMessage('Account Information has been updated');
        } else {
            showErrorMessage(mutation.data?.message);
        }
    }, [mutation.isLoading, mutation.data, queryClient]);

    useEffect(() => {
        if (information.data) {
            formData.current = {
                firstName: information.data.firstName,
                lastName: information.data.lastName,
                phone: information.data.phone ? formatPhoneNumber(information.data.phone) : '',
                email: information.data.email,
                userName: information.data.userName
            };

            if (information.data.avatar) {
                setPreview(information.data.avatar);
            }

            updateState({});
        }
    }, [information.isLoading, information.data])

    const onSubmit = async (e: any) => {
        e.preventDefault();
        const { firstName, lastName, phone, email } = formData.current;
        const data = new FormData();
        if (selectedFile) {
            data.append('file', selectedFile as Blob, selectedFile?.name);
        }
        data.append('firstName', firstName);
        data.append('lastName', lastName);
        data.append('phone', phone);
        data.append('email', email);
        await mutation.mutate(data);
    };

    return <>
        <form onSubmit={onSubmit}>
            <Form formData={formData.current} disabled={mutation.isLoading || information.isLoading} requiredMark={""}
                showValidationSummary={true}>
                <GroupItem colCount={3}>
                    <SimpleItem render={avatarRender}>
                    </SimpleItem>
                    <GroupItem colSpan={2} colCount={2}>
                        <Item
                            colSpan={2}
                            dataField={'userName'}
                            editorType={'dxTextBox'}
                            editorOptions={userNameEditorOptions}>
                            <Label text={'User Name'} />
                        </Item>
                        <Item
                            colSpan={2}
                            dataField={'firstName'}
                            editorType={'dxTextBox'}
                            editorOptions={firstNameEditorOptions}>
                            <RequiredRule message="First Name is required" />
                            <Label text={'First Name'} />
                        </Item>
                        <Item
                            colSpan={2}
                            dataField={'lastName'}
                            editorType={'dxTextBox'}
                            editorOptions={lastNameEditorOptions}>
                            <RequiredRule message="Last Name is required" />
                            <Label text={'Last Name'} />
                        </Item>
                        <Item
                            colSpan={2}
                            dataField={'email'}
                            editorType={'dxTextBox'}
                            editorOptions={emailEditorOptions}>
                            <RequiredRule message="Email Address is required" />
                            <EmailRule message="Email is invalid" />
                            <Label text={'Email Address'} />
                        </Item>
                        <Item
                            colSpan={2}
                            dataField={'phone'}
                            editorType={'dxTextBox'}
                            editorOptions={phoneEditorOptions}
                            visible={information.data?.isInternal === false}
                        >
                            <PatternRule
                                pattern={/^[02-9]\d{2}-\d{3}-\d{4}$/}
                            />
                            <Label text={'Phone'} />
                        </Item>
                        <ButtonItem horizontalAlignment="center">
                            <ButtonOptions
                                elementAttr={submitButtonAttributes}
                                useSubmitBehavior={true}
                                width={'100%'}
                            >
                                <span className="dx-button-text">
                                    {
                                        mutation.isLoading
                                            ? <LoadIndicator width={'24px'} height={'24px'} visible={true} />
                                            : 'Save'
                                    }
                                </span>
                            </ButtonOptions>
                        </ButtonItem>
                        <ButtonItem horizontalAlignment="center">
                            <ButtonOptions
                                useSubmitBehavior={false}
                                width={'100%'}
                                disabled={mutation.isLoading}
                                onClick={() => {
                                    navigate('/home');
                                }}
                            >
                                <span className="dx-button-text">Cancel</span>
                            </ButtonOptions>
                        </ButtonItem>
                    </GroupItem>
                </GroupItem>
                <ValidationSummary id="summary"></ValidationSummary>
            </Form>
        </form>
    </>;

    function avatarRender() {
        return (
            <div style={{ textAlign: 'center', marginTop: '10px', paddingLeft: '20px' }}>
                <div className="form-avatar"
                    style={{ backgroundImage: (preview === undefined ? 'none' : `url('` + preview + `')`) }}>
                </div>
                <div className="my-account-photo-uploader">
                    <FileUploader
                        selectButtonText="Change Photo"
                        labelText=""
                        accept="image/*"
                        uploadMode="useForm"
                        showFileList={false}
                        onValueChanged={(e) => {
                            if (e.value) {
                                setSelectedFile(e.value[0]);
                            }
                        }}

                    />
                </div>
            </div>
        );
    }
}


